import { useMemo } from 'react';
import { buildImgixSrcSet, isImgixUrl } from './buildImgixSrcSet';
import { DESKTOP_IMAGE_MEDIA } from './imageSizing';
import type { ImageSizing } from './imageSizing';
import { useImageTransform } from './ImageTransformContext';
import { StyledImage, StyledPicture } from './EntityImage.styles';
import type { EntityImageProps } from './EntityImage.types';

export function EntityImage({
  src,
  alt = '',
  loading = 'lazy',
  transform,
  sizing,
  mobileSizing,
  ...rest
}: EntityImageProps) {
  const contextTransform = useImageTransform();
  const applyTransform = transform ?? contextTransform;

  const { resolvedSrc, srcSet, desktopSrcSet } = useMemo(() => {
    const resolve = (target?: ImageSizing) =>
      src && applyTransform && !isImgixUrl(src) ? applyTransform(src, target) : src;

    const resolved = resolve(mobileSizing ?? sizing);
    const desktop = mobileSizing ? resolve(sizing) : undefined;

    return {
      resolvedSrc: resolved,
      srcSet: buildImgixSrcSet(resolved),
      desktopSrcSet:
        desktop && desktop !== resolved ? buildImgixSrcSet(desktop) ?? desktop : undefined,
    };
  }, [src, applyTransform, sizing, mobileSizing]);

  const image = (
    <StyledImage
      src={resolvedSrc}
      srcSet={srcSet}
      alt={alt}
      loading={loading}
      decoding="async"
      {...rest}
    />
  );

  if (!desktopSrcSet) return image;

  return (
    <StyledPicture>
      <source media={DESKTOP_IMAGE_MEDIA} srcSet={desktopSrcSet} />
      {image}
    </StyledPicture>
  );
}
